import rawCatalog from '../assets/wall_catalog.json';

/**
 * Murs de l'éditeur de cartes (table `map_walls`) — rendu pixel-art LimeZu
 * (Room_Builder) avec assemblage automatique selon les voisins, généré par
 * `tools/assets-cli/src/ingest-walls.ts`.
 */
export interface WallCatalogEntry {
  /** `lz_wall_{col}_{row}` — position du style dans Room_Builder_32x32.png. */
  id: string;
  label: string;
  /** Frame de l'atlas : haut de face, variante extrémité gauche (voir WallVariant). */
  firstFrame: number;
  /**
   * Mur noir (bordure de carte, vide autour des pièces) : toujours la brique
   * sombre, jamais de face ni de débordement vers le haut.
   */
  solid?: boolean;
  /**
   * Dessus de mur propre au style (murs verticaux, débordement vers le haut).
   * Absent : le dessus sombre commun du Room_Builder (`bodyFrame` du catalogue).
   * Murs du monde du campus de Charles (`ch_*`) : rangée 2 de leur planche.
   */
  bodyFrame?: number;
}

interface WallCatalogFile {
  atlas: { url: string; tileSize: number; columns: number };
  bodyFrame: number;
  styles: WallCatalogEntry[];
  aliases: Record<string, string>;
}

const catalogFile = rawCatalog as WallCatalogFile;

/** Atlas à charger en spritesheet Phaser (frames `tileSize`×`tileSize`). */
export const WALL_ATLAS = catalogFile.atlas;

export const wallCatalog: WallCatalogEntry[] = catalogFile.styles;

/** Style de repli pour un ID inconnu : un vrai mur, jamais le mur noir (premier de la palette). */
const DEFAULT_STYLE = wallCatalog.find((s) => !s.solid)!;

const byId = new Map<string, WallCatalogEntry>(wallCatalog.map((s) => [s.id, s]));
for (const [alias, target] of Object.entries(catalogFile.aliases)) {
  const entry = byId.get(target);
  if (entry) byId.set(alias, entry);
}

/** Résout un style (ou un ancien ID placeholder `wall_generic`/`wall_glass`). */
export function findWallStyle(id: string): WallCatalogEntry | undefined {
  return byId.get(id);
}

/** Frame d'aperçu (palette) : pan isolé, haut de face. */
export function wallPreviewFrames(style: WallCatalogEntry): { upper: number | null; lower: number } {
  if (style.solid) return { upper: null, lower: catalogFile.bodyFrame };
  return { upper: style.firstFrame + 3, lower: style.firstFrame + 3 + WALL_ATLAS.columns };
}

export interface WallCell {
  tileX: number;
  tileY: number;
  wallStyleId: string;
}

export interface WallTile {
  tileX: number;
  tileY: number;
  frame: number;
  /**
   * Rangée de la cellule peinte qui "possède" cette tuile — le tri en Y doit
   * utiliser le bas de CETTE cellule, y compris pour la tuile qui déborde sur
   * la case du dessus : un avatar qui s'y tient (case non bloquante) doit
   * passer derrière le mur.
   */
  ownerTileY: number;
}

// Variantes horizontales d'un style (colonnes de l'atlas).
const LEFT_END = 0;
const MIDDLE = 1;
const RIGHT_END = 2;
const SINGLE = 3;

/**
 * Assemblage automatique des murs, calqué sur les murs peints dans Tiled sur
 * les cartes existantes (lobby) :
 *
 * - Chaque cellule peinte est la partie BLOQUANTE du mur (collision inchangée).
 * - Cellule "face" (aucun mur ni au-dessus ni en dessous → mur horizontal) :
 *   bas de face (plinthe) dans la cellule, haut de face (avec liseré) dans la
 *   case du dessus — case non bloquante, l'avatar venant du nord passe derrière.
 *   Extrémités gauche/droite choisies selon les voisines "face" du même style.
 * - Cellule "corps" (mur au-dessus ou en dessous → mur vertical, ou jonction) :
 *   dessus sombre ; si rien au-dessus, le sombre déborde aussi d'une case vers
 *   le haut pour s'aligner sur le haut des faces voisines.
 * - Mur noir (`solid`) : brique sombre dans sa case, rien d'autre ; ignoré
 *   dans le choix face/colonne des murs normaux voisins.
 *
 * Conséquence pour l'admin : un mur horizontal se peint sur UNE rangée (celle
 * qui bloque) ; sa face monte visuellement d'une case au-dessus.
 */
export function computeWallTiles(cells: WallCell[]): WallTile[] {
  const key = (x: number, y: number) => `${x},${y}`;
  const byPos = new Map<string, WallCell>();
  for (const c of cells) byPos.set(key(c.tileX, c.tileY), c);

  const isSolid = (c: WallCell | undefined) => !!c && !!findWallStyle(c.wallStyleId)?.solid;
  const has = (x: number, y: number) => byPos.has(key(x, y));
  // Les murs noirs ne comptent pas comme voisins pour décider face/colonne :
  // un mur horizontal posé juste sous une bordure noire garde sa face (elle
  // se détache sur le noir, comme dans Gather) au lieu de devenir sombre.
  const hasNormal = (x: number, y: number) => { const n = byPos.get(key(x, y)); return !!n && !isSolid(n); };
  const isFace = (x: number, y: number) => hasNormal(x, y) && !hasNormal(x, y - 1) && !hasNormal(x, y + 1);

  const tiles: WallTile[] = [];
  for (const c of cells) {
    const { tileX: x, tileY: y } = c;
    const style = findWallStyle(c.wallStyleId) ?? DEFAULT_STYLE;

    if (style.solid) {
      tiles.push({ tileX: x, tileY: y, frame: catalogFile.bodyFrame, ownerTileY: y });
      continue;
    }

    if (!isFace(x, y)) {
      const body = style.bodyFrame ?? catalogFile.bodyFrame;
      tiles.push({ tileX: x, tileY: y, frame: body, ownerTileY: y });
      if (!has(x, y - 1)) {
        tiles.push({ tileX: x, tileY: y - 1, frame: body, ownerTileY: y });
      }
      continue;
    }

    // Une voisine d'un autre style est traitée comme une fin de mur : la
    // bordure sombre de chaque extrémité marque la séparation.
    const sameFace = (nx: number) => {
      const n = byPos.get(key(nx, y));
      return !!n && isFace(nx, y) && (findWallStyle(n.wallStyleId) ?? DEFAULT_STYLE) === style;
    };
    const left = sameFace(x - 1);
    const right = sameFace(x + 1);
    const variant = left && right ? MIDDLE : left ? RIGHT_END : right ? LEFT_END : SINGLE;

    const upper = style.firstFrame + variant;
    tiles.push({ tileX: x, tileY: y - 1, frame: upper, ownerTileY: y });
    tiles.push({ tileX: x, tileY: y, frame: upper + WALL_ATLAS.columns, ownerTileY: y });
  }
  return tiles;
}
